/**
 * Bottom Navigation Component
 *
 * Fixed bottom tab navigation for mobile.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib';
import { useUIStore } from '@/state/stores/ui.store';
import { useNavigationItems } from '../../hooks/use-navigation-items';
import { DRAWER_IDS } from '../../types';
import type { BottomNavProps } from '../../types';

export const BottomNav = ({ className }: BottomNavProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openDrawer } = useUIStore();
  const { bottomNavItems, isActive } = useNavigationItems();

  const handleItemClick = (item: (typeof bottomNavItems)[0]) => {
    if (item.id === 'more') {
      openDrawer(DRAWER_IDS.MOBILE_NAV);
    } else {
      navigate(item.path);
    }
  };

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-neutral-200',
        'safe-area-inset-bottom',
        className
      )}
    >
      <div className="h-16 flex items-stretch">
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const label = t(item.labelKey, item.label);

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-1 transition-colors',
                active
                  ? 'text-primary-500'
                  : 'text-neutral-500 hover:text-neutral-700'
              )}
            >
              <Icon
                className={cn(
                  'w-5 h-5',
                  active && 'text-primary-500'
                )}
              />
              <span
                className={cn(
                  'text-xs font-medium',
                  active && 'text-primary-500'
                )}
              >
                {label}
              </span>

              {/* Badge */}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute top-2 right-1/2 translate-x-3 px-1.5 py-0.5 text-xs font-medium rounded-full bg-primary-500 text-white">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
